import { Controller, useFormContext } from 'react-hook-form';
import CandidatesDateFieldForForm from '@/features/agent/candidates/components/CandidatesDateFieldForForm';
import MultiSelect from '@/features/agent/candidates/components/MultiSelect';
import { COMPANY_PHASE_ITEMS, LISTED_PHASE_ITEMS } from '../constants';
import Input from '@/components/Input';
import { MaterialIcon } from '@/components/MaterialIcon';
import Select from '@/components/Select';
import type { CompaniesCreateInput } from '../schemas/companiesCreateSchema';
import { Textarea } from '@/components/Textarea';
import type { SharedCustomField } from '@/types/serializers';
import type AgentApiCompaniesMasterData from '@/types/serializers/AgentApiCompaniesMasterData';
import { useState } from 'react';
import CompaniesDatabaseImportModal from './CompaniesDatabaseImportModal';
import { Button } from '@/components/Button';

// 入力文字列から数字以外を除去する。type="number" は IME（日本語変換）を防げないため、
// type="text" + inputMode にしたうえで入力値そのものをサニタイズして数字だけ残す。
const sanitizeNumeric = (value: string, allowDecimal: boolean) => {
  if (!allowDecimal) return value.replace(/\D/g, '');
  // 数字とドット以外を除去し、ドットは最初の1つだけ残す（`1..2` → `1.2`）
  const cleaned = value.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

type Props = {
  industries: { id: number; name: string }[];
  customFields: { id: number; name: string }[];
  onSubmit: () => void;
  loading?: boolean;
};

export default function CompaniesCreateForm({
  industries,
  customFields: _customFields,
  onSubmit,
}: Props) {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<CompaniesCreateInput>();
  const [isDbModalOpen, setIsDbModalOpen] = useState(false);

  const handleImportDecide = (item: AgentApiCompaniesMasterData) => {
    // 基本情報反映
    setValue('name', item.name ?? '');
    setValue('company_information.official_url', item.official_url ?? '');
    setValue('company_information.address', item.address ?? '');
    if (item.established_info) {
      const matches = item.established_info.match(/(\d{4}).+(\d{1,2}).+(\d{1,2})/);
      if (matches) {
        setValue(
          'company_information.established_date',
          `${matches[1]}-${matches[2]}-${matches[3]}`
        );
      } else {
        setValue('company_information.established_date', '');
      }
    }
    setValue(
      'company_information.number_of_employees',
      item.number_of_employee ? Number(item.number_of_employee) : 0
    );
    setValue('company_information.corporate_number', item.corporate_number ?? '');
    setValue('company_information.listed_phase', item.listed_phase);
    setValue('company_information.description', item.description ?? '');
    setValue('company_information.industry_id', item.industry_id);

    setIsDbModalOpen(false);
  };

  return (
    <form
      className="mb-20 flex flex-col gap-md"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <section className="rounded border border-border bg-white p-md">
        <div className="mb-4 flex items-center">
          <h2 className="text-heading-md">基本情報</h2>
          <div className="ml-auto">
            <Button type="button" onClick={() => setIsDbModalOpen(true)} color="secondary">
              <MaterialIcon name="download" className="mr-1 text-xl!" />
              データベースから読み込み
              <MaterialIcon name="keyboard_arrow_right" className="ml-1 text-xs!" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-md">
          <div>
            <label className="mb-xs block text-heading-xxs">
              企業名
              <span className="ml-2 inline-flex h-5 items-center rounded-full bg-danger_lighten px-3 text-xs leading-none font-bold text-danger">
                必須
              </span>
            </label>
            <Input
              autoComplete="off"
              className="w-full"
              error={!!errors?.name}
              {...register('name')}
            />
            {errors?.name && (
              <p role="alert" className="mt-xxs text-body-xs text-danger">
                {String(errors.name.message)}
              </p>
            )}
          </div>
          <div>
            <label className="mb-xs block text-heading-xxs">代表者名</label>
            <Input
              autoComplete="off"
              className="w-full"
              {...register('company_information.president_name')}
            />
          </div>

          <div>
            <label className="mb-xs block text-heading-xxs">URL</label>
            <Input
              autoComplete="off"
              className="w-full"
              error={!!errors.company_information?.official_url}
              {...register('company_information.official_url')}
            />
            {errors.company_information?.official_url && (
              <p role="alert" className="mt-xxs text-body-xs text-danger">
                {String(errors.company_information.official_url.message)}
              </p>
            )}
          </div>
          <div>
            <label className="mb-xs block text-heading-xxs">
              住所
              <span className="ml-2 inline-flex h-5 items-center rounded-full bg-danger_lighten px-3 text-xs leading-none font-bold text-danger">
                必須
              </span>
            </label>
            <Input
              autoComplete="off"
              className="w-full"
              error={!!errors.company_information?.address}
              {...register('company_information.address')}
            />
            {errors.company_information?.address && (
              <p role="alert" className="mt-xxs text-body-xs text-danger">
                {String(errors.company_information.address.message)}
              </p>
            )}
          </div>

          <div>
            <label className="mb-xs block text-heading-xxs">設立</label>
            <Controller
              control={control}
              name="company_information.established_date"
              render={({ field: { value, onChange } }) => (
                <CandidatesDateFieldForForm
                  value={value ?? ''}
                  onChange={(date) => onChange(date ?? '')}
                  className="w-full"
                  placeholder="YYYY/MM/DD"
                />
              )}
            />
            {errors.company_information?.established_date && (
              <p role="alert" className="mt-xxs text-body-xs text-danger">
                {String(errors.company_information.established_date.message)}
              </p>
            )}
          </div>
          <div>
            <label className="mb-xs block text-heading-xxs">企業フェーズ</label>
            <Controller
              control={control}
              name="company_information.phase"
              render={({ field: { value, onChange } }) => (
                <Select
                  items={COMPANY_PHASE_ITEMS}
                  defaultLabel=""
                  value={value ?? undefined}
                  onValueChange={(v) => onChange(v)}
                />
              )}
            />
          </div>

          <div>
            <label className="mb-xs block text-heading-xxs">上場情報</label>
            <Controller
              control={control}
              name="company_information.listed_phase"
              render={({ field: { value, onChange } }) => (
                <Select
                  items={LISTED_PHASE_ITEMS.map((item) => ({
                    value: item.value,
                    label: item.label,
                  }))}
                  defaultLabel=""
                  value={value ?? undefined}
                  onValueChange={(v) => onChange(v)}
                />
              )}
            />
          </div>
          <div>
            <label className="mb-xs block text-heading-xxs">業種</label>
            <Controller
              control={control}
              name="company_information.industry_id"
              render={({ field: { value, onChange } }) => (
                <Select
                  items={industries.map((i) => ({ value: i.id, label: i.name }))}
                  defaultLabel=""
                  value={value == null ? undefined : String(value)}
                  onValueChange={(v) => onChange(Number(v))}
                />
              )}
            />
          </div>

          <div>
            <label className="mb-xs block text-heading-xxs">従業員数</label>
            {(() => {
              const reg = register('company_information.number_of_employees', {
                setValueAs: (v) => (v === '' ? null : Number(v)),
              });
              return (
                <Input
                  className="w-full"
                  type="text"
                  inputMode="numeric"
                  {...reg}
                  onChange={(e) => {
                    e.target.value = sanitizeNumeric(e.target.value, false);
                    reg.onChange(e);
                  }}
                />
              );
            })()}
            {errors.company_information?.number_of_employees && (
              <p role="alert" className="mt-xxs text-body-xs text-danger">
                {String(errors.company_information.number_of_employees.message)}
              </p>
            )}
          </div>
          <div>
            <label className="mb-xs block text-heading-xxs">法人番号</label>
            <Input
              className="w-full"
              error={!!errors.company_information?.corporate_number}
              {...register('company_information.corporate_number')}
            />
            {errors.company_information?.corporate_number && (
              <p role="alert" className="mt-xxs text-body-xs text-danger">
                {String(errors.company_information.corporate_number.message)}
              </p>
            )}
          </div>

          <div className="col-span-2">
            <label className="mb-xs block text-heading-xxs">事業内容</label>
            <Textarea
              rows={6}
              className="min-h-[120px] w-full p-2"
              {...register('company_information.description')}
            />
          </div>
        </div>
      </section>
      <CompaniesDatabaseImportModal
        isOpen={isDbModalOpen}
        onClose={() => setIsDbModalOpen(false)}
        onDecide={handleImportDecide}
      />

      <section className="rounded border border-border bg-white p-md">
        <h2 className="mb-4 text-heading-md">契約情報</h2>
        <div className="grid grid-cols-3 gap-md">
          <div>
            <label className="mb-xs block text-heading-xxs">契約状況</label>
            <Controller
              control={control}
              name="company_contract.contract_status"
              render={({ field: { value, onChange } }) => (
                <Select
                  items={[
                    { value: 'contracted', label: '契約中' },
                    { value: 'nothing', label: '未契約' },
                    { value: 'other', label: 'その他' },
                  ]}
                  defaultLabel=""
                  value={value ?? undefined}
                  onValueChange={onChange}
                />
              )}
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-xs block text-heading-xxs">手数料</label>
            <div className="flex items-center gap-md">
              <div className="flex-1">
                {(() => {
                  const reg = register('company_contract.fee_rate', {
                    setValueAs: (v) => (v === '' ? undefined : Number(v)),
                  });
                  return (
                    <Input
                      className="w-full"
                      type="text"
                      inputMode="decimal"
                      {...reg}
                      onChange={(e) => {
                        e.target.value = sanitizeNumeric(e.target.value, true);
                        reg.onChange(e);
                      }}
                    />
                  );
                })()}
              </div>
              <span className="whitespace-nowrap">%</span>
            </div>
          </div>
          <div className="flex flex-col">
            <label className="mb-xs block text-heading-xxs">ミニマムフィー</label>
            <div className="flex items-center gap-md">
              <div className="flex-1">
                {(() => {
                  const reg = register('company_contract.minimum_fee', {
                    setValueAs: (v) => (v === '' ? undefined : Number(v)),
                  });
                  return (
                    <Input
                      className="w-full"
                      type="text"
                      inputMode="numeric"
                      {...reg}
                      onChange={(e) => {
                        e.target.value = sanitizeNumeric(e.target.value, false);
                        reg.onChange(e);
                      }}
                    />
                  );
                })()}
              </div>
              <span className="whitespace-nowrap">万円</span>
            </div>
          </div>
          <div className="col-span-3">
            <label className="mb-xs block text-heading-xxs">備考</label>
            <Textarea
              rows={6}
              className="min-h-[79px] w-full p-2"
              {...register('company_contract.note')}
            />
          </div>
        </div>
      </section>

      {Array.isArray(_customFields as SharedCustomField[]) &&
        (_customFields as SharedCustomField[]).length > 0 &&
        Object.entries(
          (_customFields as SharedCustomField[]).reduce(
            (acc: Record<string, SharedCustomField[]>, field: SharedCustomField) => {
              const key = field.custom_category?.name || 'その他';
              acc[key] = acc[key] || [];
              acc[key].push(field);
              return acc;
            },
            {}
          )
        ).map(([categoryName, fields], idx) => (
          <section key={idx} className="rounded border border-border bg-white p-md">
            <h2 className="mb-4 text-heading-md">{categoryName}</h2>
            <div className="grid grid-cols-2 gap-md">
              {(fields as SharedCustomField[])
                .sort((a, b) => a.order_number - b.order_number)
                .map((field: SharedCustomField) => (
                  <Controller
                    key={field.id}
                    name={`customData.${field.id}` as const}
                    render={({ field: ctrl, fieldState }) => {
                      const errorMessage = fieldState.error?.message;
                      const label = (
                        <label className="mb-xs block text-heading-xxs">
                          {field.name}
                          {field.is_required && (
                            <span className="ml-2 inline-flex h-5 items-center rounded-full bg-danger_lighten px-3 text-xs leading-none font-bold text-danger">
                              必須
                            </span>
                          )}
                        </label>
                      );
                      const input = (() => {
                        switch (field.field_type) {
                          case 'short_text':
                            return (
                              <Input
                                className="w-full"
                                autoComplete="off"
                                error={!!errorMessage}
                                value={(ctrl.value as string) || ''}
                                onChange={(e) => ctrl.onChange(e.target.value)}
                                onBlur={ctrl.onBlur}
                              />
                            );
                          case 'long_text':
                            return (
                              <Textarea
                                className="w-full p-3"
                                rows={4}
                                isError={!!errorMessage}
                                value={(ctrl.value as string) || ''}
                                onChange={(e) => ctrl.onChange(e.target.value)}
                                onBlur={ctrl.onBlur}
                              />
                            );
                          case 'date':
                            // 選択系コンポーネントは onBlur を発火しないため、値変更時に
                            // 明示的に onBlur を呼び blur バリデーション（エラー文言表示）をトリガーする
                            return (
                              <CandidatesDateFieldForForm
                                className="w-full"
                                value={(ctrl.value as string) || ''}
                                onChange={(v) => {
                                  ctrl.onChange(v);
                                  ctrl.onBlur();
                                }}
                                placeholder="YYYY/MM/DD"
                              />
                            );
                          case 'single_select':
                            return (
                              <Select
                                className="w-full"
                                items={(field.custom_field_options || []).map(
                                  (o: { id: number; value: string }) => ({
                                    value: o.id,
                                    label: o.value,
                                  })
                                )}
                                value={ctrl.value as string | undefined}
                                onValueChange={(v) => {
                                  ctrl.onChange(v);
                                  ctrl.onBlur();
                                }}
                              />
                            );
                          case 'multiple_select':
                            // 候補者画面と同じく、選択肢ラベル(option.value)をカンマ連結した文字列で保持する
                            return (
                              <MultiSelect
                                customFieldOptions={(field.custom_field_options || []).map(
                                  (o: { id: number; value: string }) => ({
                                    id: o.id,
                                    value: o.value,
                                  })
                                )}
                                initialValues={(ctrl.value as string) || ''}
                                onChange={(selectedOptions) => {
                                  ctrl.onChange(selectedOptions.map((o) => o.value).join(','));
                                  ctrl.onBlur();
                                }}
                              />
                            );
                          case 'number':
                            return (
                              <Input
                                className="w-full"
                                autoComplete="off"
                                type="text"
                                inputMode="numeric"
                                error={!!errorMessage}
                                value={(ctrl.value as string) || ''}
                                onChange={(e) => {
                                  const v = sanitizeNumeric(e.target.value, false);
                                  ctrl.onChange(v === '' ? undefined : Number(v));
                                }}
                                onBlur={ctrl.onBlur}
                              />
                            );
                          default:
                            return (
                              <Input
                                className="w-full"
                                autoComplete="off"
                                error={!!errorMessage}
                                value={(ctrl.value as string) || ''}
                                onChange={(e) => ctrl.onChange(e.target.value)}
                                onBlur={ctrl.onBlur}
                              />
                            );
                        }
                      })();
                      return (
                        <div>
                          {label}
                          {input}
                          {errorMessage && (
                            <p role="alert" className="mt-xxs text-body-xs text-danger">
                              {errorMessage}
                            </p>
                          )}
                        </div>
                      );
                    }}
                  />
                ))}
            </div>
          </section>
        ))}
    </form>
  );
}
